import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { setGlobalConfig } from 'actions/global.config.action'
import { GlobalConfigType } from 'actions/global.config.type'
import {
  getDateHeaders,
  getItemsForField,
  getPlaceholderFromTitle,
  getPlaceholderValue,
  hasPlaceholderPrefix,
  IReportHeaders,
} from 'pages/Loan/Reports/types'
import {
  isPipelineHeaderEmailAll,
  PIPELINE_ACTIONS_TITLE,
  PIPELINE_HEADER_EMAIL_ALL,
} from 'pages/Pipeline/constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getLoanReportsPlaceholders } from 'services'
import { svgSearch } from 'stories/assets'
import { Input2, Modal, Tooltip } from 'stories/components'
import { hasXorItem } from 'utils'

export type HeaderCell = string

export type HeaderRow = HeaderCell[]

export interface HeaderSelectionModalProps {
  isOpen: boolean
  type?: 'all' | 'date'
  multiselect?: boolean
  headers?: HeaderRow[] | null
  selectedCell?: { rowIndex: number; cellIndex: number } | string | null
  /** Hide specific placeholder labels from the picker. Used to hide "Sub Status" when no sub-status is configured. */
  hidePlaceholders?: string[]
  onClose: () => void
  onSelectPlaceholder: (value: string, title: string) => void
  onItemsSubmit: (selectedValue: string, selectedItems: string[]) => void
  checkSelected: (v: string, title: string) => boolean
}

export const HeaderSelectionModal = ({
  isOpen,
  type = 'all',
  multiselect = true,
  headers = null,
  selectedCell = null,
  hidePlaceholders = [],
  onClose,
  onSelectPlaceholder,
  onItemsSubmit,
  checkSelected,
}: HeaderSelectionModalProps) => {
  const dispatch = useDispatch()
  const cachedPlaceholders = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.ReportPlaceholders],
  ) as Record<string, IReportHeaders[]>
  const [query, setQuery] = useState('')
  const [collapseStatus, setCollapseStatus] = useState<Record<string, boolean>>({})
  const [modalItems, setModalItems] = useState<Record<string, any>>({})
  const [modalSelectedItems, setModalSelectedItems] = useState<string[]>([])
  const [modalSelectedValue, setModalSelectedValue] = useState('')
  const [modalTitle, setModalTitle] = useState('')
  const [placeholders, setPlaceholders] = useState<Record<string, IReportHeaders[]>>(
    cachedPlaceholders?.Loan ? cachedPlaceholders : { Loan: [] },
  )
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (cachedPlaceholders?.Loan?.length > 0) {
      setPlaceholders(cachedPlaceholders)
    } else {
      setIsLoading(true)
      getLoanReportsPlaceholders().then((data) => {
        dispatch(setGlobalConfig(GlobalConfigType.ReportPlaceholders, data))
        setPlaceholders(data)
        setIsLoading(false)
      })
    }
  }, [])

  const filteredPlaceholdersByType = useMemo(() => {
    if (type === 'date') return getDateHeaders(placeholders)
    return null
  }, [placeholders, type])

  useEffect(() => {
    if (placeholders.Loan && isOpen) {
      const temp: Record<string, boolean> = {}
      placeholders.Loan.forEach((item) => {
        temp[item.title] = true
      })

      if (selectedCell && typeof selectedCell === 'object' && headers) {
        const currentValue = headers[selectedCell.rowIndex]?.[selectedCell.cellIndex]
        if (currentValue) {
          for (const item of placeholders.Loan) {
            const hasPrefix = hasPlaceholderPrefix(item.title)
            const hasItems = !!(item.items || item.subItems)
            const found = item.data.some((v) => {
              if (hasItems) return currentValue.includes(`${v}-`)
              if (hasPrefix) {
                return (
                  currentValue === getPlaceholderValue(item.title, v) || currentValue.startsWith(`${item.title} - `)
                )
              }
              return currentValue === v
            })

            if (found) {
              temp[item.title] = false
              break
            }
          }
        }
      }
      if (selectedCell && typeof selectedCell === 'string') {
        placeholders.Loan.some((item) => {
          const isFound = item.data.some((v) => getPlaceholderValue(item.title, v) === selectedCell)
          if (isFound) {
            temp[item.title] = false
            return true
          }
          return false
        })
      }

      setCollapseStatus(temp)
    }
  }, [placeholders, isOpen, selectedCell, headers])

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setModalSelectedValue('')
      setModalTitle('')
      setModalItems({})
      setModalSelectedItems([])
    }
  }, [isOpen])

  useEffect(() => {
    if (query && placeholders.Loan) {
      const temp: Record<string, boolean> = {}
      placeholders.Loan.forEach((item) => {
        temp[item.title] = false
      })
      setCollapseStatus(temp)
    }
  }, [query, placeholders.Loan])

  const filteredPlaceholders = useMemo(() => {
    let list = placeholders.Loan || []
    // Expose "Tags" as a pickable pipeline column. The grid renders it via an
    // inline tag editor (LoanTags); the backend should also echo it in
    // pipelineHeaders so the selection persists across reloads.
    if (type !== 'date' && !list.some((h) => h.data.includes('Tags'))) {
      list = [...list, { title: 'Tags', data: ['Tags'] }]
    }
    if (hidePlaceholders.length > 0) {
      const hideSet = new Set(hidePlaceholders)
      list = list.map((header) => ({
        ...header,
        data: header.data.filter((v) => !hideSet.has(v)),
      }))
    }
    if (query) {
      const lowerQuery = query.toLowerCase()
      list = list.map((header) => ({
        ...header,
        data: header.data.filter((v) => v.toLowerCase().includes(lowerQuery)).sort(),
      }))
    }

    if (filteredPlaceholdersByType) {
      list = list
        .filter((headers) => hasXorItem(headers.data, filteredPlaceholdersByType))
        .map((headers) => ({
          ...headers,
          data: headers.data.filter((v) => filteredPlaceholdersByType.includes(v)),
        }))
    }

    return list
  }, [placeholders, query, filteredPlaceholdersByType, hidePlaceholders])

  const isModalSelectedAll = useMemo(() => {
    const keys = Object.keys(modalItems)
    return keys.length > 0 && keys.length === modalSelectedItems.length
  }, [modalItems, modalSelectedItems])

  const onToggle = useCallback((title: string) => {
    setCollapseStatus((prev) => ({ ...prev, [title]: !prev[title] }))
  }, [])

  const handleSelectPlaceholder = useCallback(
    (value: string, title: string) => {
      const header = getPlaceholderFromTitle(placeholders.Loan, title)
      if (!header) return

      const items = getItemsForField(header, value)
      if (items) {
        setModalSelectedValue(value)
        setModalTitle(header.customTitles?.[value] || value)
        setModalItems(items)
        const existing = (headers || [])
          .flat()
          .filter((key) => key.includes(`${value}-`))
          .map((key) => key.split('-')[1])
        setModalSelectedItems(existing)
        return
      }
      onSelectPlaceholder(value, title)
    },
    [placeholders, headers, onSelectPlaceholder],
  )

  const handleToggleModalItem = useCallback(
    (value: string) => {
      if (!multiselect) {
        onSelectPlaceholder(`${modalSelectedValue}-${value}`, modalTitle)
        return
      }
      setModalSelectedItems((prev) => {
        const idx = prev.indexOf(value)
        if (idx === -1) return [...prev, value]
        return prev.filter((_, i) => i !== idx)
      })
    },
    [multiselect, modalSelectedValue, modalTitle, onSelectPlaceholder],
  )

  const handleModalSubmit = useCallback(() => {
    if (!modalSelectedValue || !selectedCell) return
    onItemsSubmit(modalSelectedValue, modalSelectedItems)
    setModalSelectedItems([])
    setModalSelectedValue('')
    setModalTitle('')
    setModalItems({})
  }, [modalSelectedValue, modalSelectedItems, selectedCell, onItemsSubmit])

  if (!isOpen) return null

  return (
    <Modal
      title={modalSelectedValue ? `${modalTitle} Details` : 'Select Header'}
      isOpen
      loading={isLoading}
      onClose={onClose}
      titleOkay={multiselect ? 'Okay' : ''}
      onOk={modalSelectedValue ? handleModalSubmit : undefined}
    >
      <div className="w-[600px] max-h-[600px] overflow-y-auto">
        {modalSelectedValue ? (
          <div>
            {multiselect && (
              <div className="flex justify-end mb-2">
                <span
                  className="text-sm cursor-pointer text-shade-blue hover:underline font-normal"
                  onClick={() => setModalSelectedItems(isModalSelectedAll ? [] : Object.keys(modalItems))}
                >
                  {isModalSelectedAll ? 'Unselect All' : 'Select All'}
                </span>
              </div>
            )}
            <div className="grid grid-cols-4">
              {Object.entries(modalItems).map(([key, label], index) => {
                const isSelected = modalSelectedItems.includes(key)
                return (
                  <div
                    key={key}
                    className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                      isSelected ? 'bg-gray-200' : ''
                    }`}
                    onClick={() => handleToggleModalItem(key)}
                  >
                    <CheckIcon className={`${isSelected ? 'text-shade-blue' : 'text-gray-300'} w-4 h-4`} />
                    <p className="text-sm">
                      {index + 1}. {label}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2 gap-x-4">
              <div className="flex-1">
                <Input2 type="search" title="Search" hasIcon icon={svgSearch} value={query} onChange={setQuery} />
              </div>
            </div>
            <div className="grid grid-cols-2 text-sm">
              <div className="col-span-full">
                <p className="flex items-center gap-2 px-2 py-1 bg-gray-100 font-semibold">{PIPELINE_ACTIONS_TITLE}</p>
                <div
                  className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                    selectedCell &&
                    typeof selectedCell === 'object' &&
                    headers &&
                    isPipelineHeaderEmailAll(headers[selectedCell.rowIndex]?.[selectedCell.cellIndex])
                      ? 'bg-gray-200'
                      : ''
                  }`}
                  onClick={() => onSelectPlaceholder(PIPELINE_HEADER_EMAIL_ALL, PIPELINE_ACTIONS_TITLE)}
                >
                  <CheckIcon
                    className={`${
                      selectedCell &&
                      typeof selectedCell === 'object' &&
                      headers &&
                      isPipelineHeaderEmailAll(headers[selectedCell.rowIndex]?.[selectedCell.cellIndex])
                        ? 'text-shade-blue'
                        : 'text-gray-300'
                    } w-4 h-4`}
                  />
                  <p className="text-sm">1. {PIPELINE_HEADER_EMAIL_ALL}</p>
                </div>
              </div>
              {filteredPlaceholders.map((header, index) => {
                const { title, data } = header
                const isOpen = !collapseStatus[title]

                return (
                  <div key={title} className="col-span-full">
                    <p className="flex items-center gap-2 px-2 py-1 bg-gray-100 font-semibold cursor-pointer">
                      <span className="flex-1" onClick={() => onToggle(title)}>
                        {index + 1}. {title}
                      </span>
                      {isOpen ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                    </p>
                    {isOpen &&
                      [...new Set(data)].map((v, i) => {
                        const isSelected = checkSelected(v, title)
                        const itemsMap = getItemsForField(header, v)
                        const hasItems = !!itemsMap
                        let tooltip = ''
                        if (hasItems) {
                          const selectedSubLabels = (headers || [])
                            .flat()
                            .filter((key) => key.includes(`${v}-`))
                            .map((key) => itemsMap[key.split('-')[1]] || key.split('-')[1])
                            .sort()

                          selectedSubLabels.forEach((item, idx) => {
                            if (idx) tooltip += idx % 5 === 0 ? ',\n' : ', '
                            tooltip += item
                          })
                        }

                        return (
                          <div
                            key={v}
                            className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                              isSelected ? 'bg-gray-200' : ''
                            }`}
                            onClick={() => handleSelectPlaceholder(v, title)}
                          >
                            <CheckIcon className={`${isSelected ? 'text-shade-blue' : 'text-gray-300'} w-4 h-4`} />
                            <Tooltip message={tooltip}>
                              <p className="text-sm">
                                {i + 1}. {header.customTitles?.[v] || v}
                              </p>
                            </Tooltip>
                          </div>
                        )
                      })}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
